import { useState, useCallback, useMemo } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Globe, Star, Megaphone, Search as SearchIcon, Bot, Users2 } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import PageHeader from "../components/PageHeader";
import GoogleSEOTab from "../components/google/GoogleSEOTab";
import GoogleGMBTab from "../components/google/GoogleGMBTab";
import GoogleAdsTab from "../components/google/GoogleAdsTab";
import GoogleAudiencesTab from "../components/google/GoogleAudiencesTab";
import GoogleAgentSidebar from "../components/google/GoogleAgentSidebar";

export default function MarketingGooglePage() {
  const { axiosAuth } = useAuth();
  const api = useMemo(() => axiosAuth(), [axiosAuth]);
  const [tab, setTab] = useState("seo");
  const [agentOpen, setAgentOpen] = useState(false);
  const [agentContext, setAgentContext] = useState({});

  const openAgentWith = useCallback((context) => {
    setAgentContext(context);
    setAgentOpen(true);
  }, []);

  return (
    <div className="p-4 md:p-6 max-w-[1600px] mx-auto pb-24" data-testid="google-marketing-page">
      <PageHeader
        icon={<Globe />}
        title="Marketing Google"
        description="SEO, fiches Google Business, Google Ads — pilote par agent IA"
      />

      <div className="flex flex-col lg:flex-row gap-4 mt-6">
        {/* Main content */}
        <div className="flex-1 min-w-0">
          <Tabs value={tab} onValueChange={setTab} className="space-y-5">
            <div className="flex items-center justify-between gap-3">
              <TabsList className="bg-[#0b0811] border border-white/10">
                <TabsTrigger value="seo" data-testid="google-tab-seo" className="data-[state=active]:bg-[#f7c935] data-[state=active]:text-[#0b0811] gap-1.5">
                  <SearchIcon className="w-3.5 h-3.5" /> SEO
                </TabsTrigger>
                <TabsTrigger value="gmb" data-testid="google-tab-gmb" className="data-[state=active]:bg-[#f7c935] data-[state=active]:text-[#0b0811] gap-1.5">
                  <Star className="w-3.5 h-3.5" /> Google Business
                </TabsTrigger>
                <TabsTrigger value="ads" data-testid="google-tab-ads" className="data-[state=active]:bg-[#f7c935] data-[state=active]:text-[#0b0811] gap-1.5">
                  <Megaphone className="w-3.5 h-3.5" /> Google Ads
                </TabsTrigger>
                <TabsTrigger value="audiences" data-testid="google-tab-audiences" className="data-[state=active]:bg-[#f7c935] data-[state=active]:text-[#0b0811] gap-1.5">
                  <Users2 className="w-3.5 h-3.5" /> Audiences IA
                </TabsTrigger>
              </TabsList>
              <Button
                onClick={() => setAgentOpen(!agentOpen)}
                className="bg-gradient-to-r from-[#4285F4] to-[#EA4335] hover:opacity-90 text-white font-bold gap-1.5"
                data-testid="toggle-agent-sidebar"
              >
                <Bot className="w-3.5 h-3.5" /> Agent IA {agentOpen ? "" : "Gemini"}
              </Button>
            </div>

            <TabsContent value="seo">
              <GoogleSEOTab api={api} onAskAgent={openAgentWith} />
            </TabsContent>
            <TabsContent value="gmb">
              <GoogleGMBTab api={api} onAskAgent={openAgentWith} />
            </TabsContent>
            <TabsContent value="ads">
              <GoogleAdsTab api={api} onAskAgent={openAgentWith} />
            </TabsContent>
            <TabsContent value="audiences">
              <GoogleAudiencesTab />
            </TabsContent>
          </Tabs>
        </div>

        {/* Agent IA sidebar */}
        {agentOpen && (
          <div className="lg:w-[400px] flex-shrink-0">
            <GoogleAgentSidebar
              api={api}
              context={agentContext}
              onClose={() => setAgentOpen(false)}
            />
          </div>
        )}
      </div>
    </div>
  );
}
